import { useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import { ComplementStrip } from "./ComplementStrip";
import { useCart } from "../context/CartContext";
import { formatCOP } from "../lib/api";
import { formatUnit } from "../lib/units";

export const CartSheet = () => {
  const { items, setQty, remove, subtotal, iva, total, isOpen, setOpen } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => { setOpen(false); navigate("/checkout"); };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="bg-white w-full sm:max-w-md flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-cm-border">
          <SheetTitle className="font-serif text-2xl flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-cm-red" /> Tu pedido
          </SheetTitle>
          <SheetDescription className="text-cm-ink-soft">
            {items.length === 0 ? "Aún no has agregado productos." : `${items.length} producto(s) en el carrito.`}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1 px-6 py-4">
          {items.length === 0 && (
            <div className="text-center py-12 text-cm-ink-soft">
              <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Explora el catálogo y agrega productos</p>
            </div>
          )}
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.codigo} className="flex gap-3 pb-4 border-b border-cm-border/60 last:border-0" data-testid={`cart-item-${it.codigo}`}>
                {it.imagen && (
                  <img src={it.imagen} alt={it.descripcion} className="h-16 w-16 rounded-md object-cover border border-cm-border" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-cm-ink-soft">{it.codigo}</p>
                  <p className="text-sm font-medium text-cm-ink line-clamp-2 leading-snug">{it.descripcion}</p>
                  <p className="text-xs text-cm-ink-soft mt-0.5">{formatUnit(it.unidad)}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setQty(it.codigo, it.cantidad - 1)}
                        className="h-7 w-7 rounded border border-cm-border hover:bg-cm-cream-alt flex items-center justify-center"
                        data-testid={`cart-decrease-${it.codigo}`}
                      ><Minus className="h-3 w-3" /></button>
                      <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${it.codigo}`}>{it.cantidad}</span>
                      <button
                        onClick={() => setQty(it.codigo, it.cantidad + 1)}
                        className="h-7 w-7 rounded border border-cm-border hover:bg-cm-cream-alt flex items-center justify-center"
                        data-testid={`cart-increase-${it.codigo}`}
                      ><Plus className="h-3 w-3" /></button>
                      <button
                        onClick={() => remove(it.codigo)}
                        className="ml-1 h-7 w-7 rounded text-cm-red hover:bg-cm-red/10 flex items-center justify-center"
                        data-testid={`cart-remove-${it.codigo}`}
                      ><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <p className="font-semibold text-sm font-mono">{formatCOP((it.precio || 0) * it.cantidad)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {items.length > 0 && (
            <ComplementStrip codigos={items.map((i) => i.codigo)} compact />
          )}
        </ScrollArea>

        {items.length > 0 && (
          <div className="border-t border-cm-border bg-cm-cream-alt/40 px-6 py-4 space-y-2">
            <div className="flex justify-between items-baseline">
              <span className="font-serif text-lg">Total</span>
              <span className="font-mono font-bold text-2xl text-cm-red" data-testid="cart-total">{formatCOP(total)}</span>
            </div>
            <p className="text-[10px] text-cm-ink-soft">Precios incluyen IVA según la tasa real de cada producto.</p>
            <Button onClick={goCheckout} className="w-full btn-cm-primary mt-2 h-11" data-testid="checkout-btn">
              Revisar y confirmar pedido <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
